/**
 * Google sign-in for shells with no native OAuth bridge (Tauri/Android).
 *
 * Electron does this in the main process (`oauth:google`), which is why the
 * Google button only ever rendered there. Everywhere else an operator with a
 * Google-created account had no way in at all: the only form on screen is
 * email+password, and those accounts have no password to type — the server
 * rejects them with OAUTH_ACCOUNT_NO_PASSWORD.
 *
 * Nothing about the server flow is Electron-specific, so this reimplements the
 * same three steps against the same endpoints, using only fetch and the
 * system-browser opener:
 *
 *   1. POST /api/auth/desktop/google/authorize  -> { state, oauthUrl }
 *   2. open oauthUrl in the SYSTEM browser (Google refuses embedded webviews),
 *      where /callback -> /exchange completes the grant and parks the session
 *      token on the state row
 *   3. poll GET /api/auth/desktop/status?state=... until it reports completed
 *
 * The operator leaves the app for the browser and comes back; polling is what
 * closes that gap, since without a native deep-link handler the browser has no
 * way to hand control back to us.
 */

import { openExternalUrl } from '@renderer/lib/runtime'

export interface GoogleHandoffResult {
  token: string
  user: { email: string; name: string }
}

export interface GoogleHandoffOptions {
  /** Fired once the browser has been opened, so the UI can explain the wait. */
  onWaiting?: () => void
  /** Abort the wait (operator pressed cancel, or navigated away). */
  signal?: AbortSignal
}

/** Thrown when the operator cancels; pages swallow this instead of toasting. */
export class GoogleOAuthCancelled extends Error {
  constructor() {
    super('Google sign-in cancelled')
    this.name = 'GoogleOAuthCancelled'
  }
}

const POLL_INTERVAL_MS = 3000
// DesktopOAuthState rows carry `expires: '5m'`. Polling past that only ever
// reports not_found, so stop when the row we're waiting on can no longer exist.
const DEADLINE_MS = 5 * 60 * 1000

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = (): void => {
      clearTimeout(timer)
      reject(new GoogleOAuthCancelled())
    }
    if (signal?.aborted) {
      clearTimeout(timer)
      reject(new GoogleOAuthCancelled())
      return
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })

export async function runGoogleOAuthHandoff(
  mode: 'login' | 'register' = 'login',
  options: GoogleHandoffOptions = {}
): Promise<GoogleHandoffResult> {
  const { onWaiting, signal } = options
  const apiUrl = process.env.VITE_API_URL

  if (signal?.aborted) throw new GoogleOAuthCancelled()

  const authorizeResponse = await fetch(`${apiUrl}/api/auth/desktop/google/authorize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ mode })
  })

  if (!authorizeResponse.ok) {
    const data = await authorizeResponse.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to start Google sign-in')
  }

  const { state, oauthUrl } = await authorizeResponse.json()
  if (!state || !oauthUrl) {
    throw new Error('Failed to start Google sign-in')
  }

  await openExternalUrl(oauthUrl)
  onWaiting?.()

  const deadline = Date.now() + DEADLINE_MS

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS, signal)

    let data: {
      status?: string
      sessionToken?: string
      user?: { email: string; name: string }
      message?: string
    }
    try {
      const statusResponse = await fetch(
        `${apiUrl}/api/auth/desktop/status?state=${encodeURIComponent(state)}`,
        { credentials: 'include' }
      )
      if (!statusResponse.ok) continue
      data = await statusResponse.json()
    } catch {
      // A booth on flaky venue wifi drops requests constantly. Keep polling —
      // the deadline is the only thing that ends this loop early.
      continue
    }

    if (data.status === 'completed') {
      if (!data.sessionToken || !data.user?.email) {
        throw new Error('Google sign-in returned an incomplete session')
      }
      return {
        token: data.sessionToken,
        user: { email: data.user.email, name: data.user.name || data.user.email }
      }
    }

    if (data.status === 'not_found' || data.status === 'expired') {
      throw new Error(data.message || 'Google sign-in expired. Please try again.')
    }
  }

  throw new Error('Google sign-in timed out. Please try again.')
}
